<template>
  <v-app class="my-application">
    <Appbar></Appbar>
    <!-- {{ $store.state.base.mobileLang.code }} -->

    <v-main>
      <PageLoader v-if="loader"></PageLoader>
      <router-view :key="$route.fullPath"></router-view>

      <v-snackbar v-model="snackbar.open" :color="snackbar.color">
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn dense text icon x-small v-bind="attrs" @click="closeSnackbar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <v-dialog v-model="disclaimerDialog" fullscreen persistent>
        <v-card class="pa-4">
          <v-card-text class="pt-4">
            Explore Ionian Islands collects user's location data to enhance the
            user experience by comparing their location to predefined points of
            interest during an active 'route' in our app even when the app is
            minimized. When the user approaches a point of interest, they
            receive a notification to inform them of their proximity. Upon
            visiting all the points of interest along the route, the user is
            awarded an achievement, celebrating their accomplishment and marking
            the route as complete.The location data collected, including
            background geolocation data, is used solely for providing
            location-based services within the App. We do not share location
            data with third parties. We do not send location data to any server
            or save it locally. The data is processed in real-time within the
            App to compare the user's location to predefined points of interest.
            Location data is stored securely and is only accessible by the App
            for the purpose of delivering the services described.
          </v-card-text>
          <v-card-actions>
            <v-btn
              style="text-transform: none"
              class="mx-auto"
              color="secondary"
              @click="onInit"
            >
              Accept
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script>
import PageLoader from "./components/PageLoader.vue";
import Appbar from "./components/Appbar.vue";
import { Device } from "@capacitor/device";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { Geolocation } from "@capacitor/geolocation";
import { SafeArea } from "capacitor-plugin-safe-area";

import { TextToSpeech } from "@capacitor-community/text-to-speech";
import { LocalNotifications } from "@capacitor/local-notifications";

import "leaflet/dist/leaflet.css";
import axios from "axios";

export default {
  components: {
    Appbar,
    PageLoader,
  },

  async created() {
    try {
      this.setCountries();
      if (this.$store.state.base.mobileLang == null) {
        const code = await Device.getLanguageCode();
        const detected = code.value.split("-")[0];
        const supported = this.$store.state.base.languages.some(
          (l) => l.code === detected,
        );
        const locale = supported ? detected : "en";

        this.setMobileLang(locale);
        this.$i18n.locale = locale;
      } else {
        axios.defaults.headers.common[
          "Accept-Language"
        ] = `${this.mobileLang.code}`;
        this.$i18n.locale = this.mobileLang.code;
      }
      await this.isLanguageSupported();
      const { platform } = await Device.getInfo();
      if (platform === "web") {
        this.disclaimerDialog = false;
        await this.onInit();
      } else if (!this.disclaimer) {
        this.disclaimerDialog = true;
      } else {
        await this.onInit();
      }

      axios.interceptors.response.use(
        (res) => {
          return res;
        },
        (err) => {
          if (err.response.status === 401) {
            this.logout().then(() => {
              this.$router.push({ path: "/login" });
            });
          }
          throw err;
        },
      );
      // App.addListener("backButton", this.$router.go(-1));
    } catch (e) {
      console.log(e);
    } finally {
      this.loader = false;
    }
  },

  data() {
    return {
      batteryLevel: null,
      eventLocation: null,
      location: null,
      loader: true,
      disclaimerDialog: false,
    };
  },

  computed: {
    ...mapState({
      mobileLang: (state) => state.base.mobileLang,
      disclaimer: (state) => state.base.disclaimer,
      token: (state) => state.auth.token,
    }),

    ...mapGetters(["isLoggedIn"]),

    snackbar: {
      get() {
        return this.$store.state.base.snackbar;
      },
      set(value) {
        this.toggleSnackbar(value);
      },
    },
  },

  methods: {
    ...mapMutations([
      "setMobileLang",
      "toggleSnackbar",
      "setInsets",
      "setCountries",
      "setPlatform",
      "setDeviceIdentifier",
    ]),
    ...mapActions(["getLocation", "isLanguageSupported", "getFavorites"]),

    closeSnackbar() {
      this.toggleSnackbar({
        open: false,
        text: null,
        color: null,
      });
    },

    async onInit() {
      this.disclaimerDialog = false;

      localStorage.setItem("disclaimer", true);

      const { platform } = await Device.getInfo();
      const { identifier } = await Device.getId();

      this.setPlatform(platform);
      this.setDeviceIdentifier(identifier);
      const res = await axios.post(`${process.env.VUE_APP_BASE_URL}/visits`, {
        visitor_status: this.isLoggedIn ? "logged_in" : "anonymous",
        device_id: identifier,
        device_type: platform,
      });

      console.log("visits", res.data);
      if (platform != "web") {
        await Geolocation.requestPermissions("location");
        await LocalNotifications.requestPermissions();
      }

      if (platform == "ios") {
        try {
          const { insets } = await SafeArea.getSafeAreaInsets();
          this.setInsets(insets);
          // Expose insets to CSS for consistent safe-area padding.
          const root = document.documentElement;
          root.style.setProperty("--safe-area-top", `${insets?.top || 0}px`);
          root.style.setProperty("--safe-area-right", `${insets?.right || 0}px`);
          root.style.setProperty("--safe-area-bottom", `${insets?.bottom || 0}px`);
          root.style.setProperty("--safe-area-left", `${insets?.left || 0}px`);
        } catch (e) {
          // Non-fatal: keep default insets (0s).
          console.log(e);
        }
      }

      if (platform == "android") await TextToSpeech.openInstall();

      await this.getLocation();

      this.toggleSnackbar({
        open: false,
        text: null,
        color: null,
      });

      if (this.isLoggedIn) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${this.token}`;
        await this.getFavorites();
      }
      this.loader = false;
    },
  },
};
</script>

<style lang="scss">
//======VUETIFY OVERRIDES========

.v-main__wrap {
  display: flex;
}

.v-toolbar__content {
  padding: 10px !important;
  justify-content: space-between !important;
  flex-grow: 1;
}

// .v-text-field--outlined fieldset {
//   border-color: #e9ecef !important;
//   border-radius: 1px !important;

//   transition: border-color 0.15s ease-in-out !important;
// }
// .v-input--is-focused fieldset {
//   border: 1px solid #ccc !important;
// }

// .v-input--selection-controls {
//   margin-top: 0px !important;
//   padding: 0px !important;
// }

.v-text-field.v-text-field--solo.v-input--dense > .v-input__control {
  min-height: 25px !important;
} //search input height

.v-icon.notranslate.mdi.mdi-circle-outline.theme--light {
  color: white !important;
} //checkbox color when unchecked

//======GLOBAL STYLES=======
.component-wrapper {
  flex-grow: 1;
  // padding-top: 10px;
}

@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700;800&display=swap");
$font-family: "Open Sans", sans-serif;
.my-application {
  *:not(.v-icon),
  *:not(.v-icon):before,
  *:not(.v-icon):after {
    font-family: $font-family !important;
  }

  //transition styles
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.2s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
